/* ============================================================================
   CRUX — MOTION LAYER  ·  assets/crux-motion.js  (Lithos pack, 2026-07-11)
   Behaviour for crux-motion.css. Vanilla classic script (IIFE), same idiom as
   crux-redesign.js. Everything fails OPEN: any error leaves the page exactly
   as server-rendered (imgs visible, sections static). Reduced motion => the
   whole file is a no-op. No layout writes outside rAF; IO-driven reveals.
   ============================================================================ */
(function () {
  'use strict';

  var REDUCE = false;
  try { REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ── LQIP marks — runs even under reduced motion (it's honesty, not
     motion): a lazy card image shows the bone ground, then fades in. */
  function initLqip() {
    var SEL = '.cx-pcard__img--front, .cx-eonrec__media img, .cx-edit__feature img, ' +
              '.cx-edit__float img, .popular-products img';
    function arm(img) {
      if (img.complete && img.naturalWidth > 0) return;   // already there — never hide
      img.classList.add('cxm-pending');
      var lift = function () {
        img.classList.remove('cxm-pending');
        img.classList.add('cxm-loaded');
      };
      img.addEventListener('load', lift, { once: true });
      img.addEventListener('error', lift, { once: true }); // alt text > hole
    }
    $all(SEL).forEach(arm);
    // safety net: whatever is still hidden 8s in becomes visible
    setTimeout(function () {
      $all('img.cxm-pending').forEach(function (img) {
        img.classList.remove('cxm-pending');
        img.classList.add('cxm-loaded');
      });
    }, 8000);
  }

  /* ── shared IntersectionObserver reveal engine ── */
  var io = null;
  function observe(el) {
    if (!io && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            en.target.classList.add('cxm-in');
            io.unobserve(en.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    if (io) observe.q.push(el), io.observe(el);
    else el.classList.add('cxm-in'); // no IO — show immediately
  }
  observe.q = [];

  /* ── 2 · blur-rise on the hero voice ── */
  function initRise() {
    var targets = [
      document.querySelector('.hero__subheading'),
      document.querySelector('.hero__heading'),
      document.querySelector('.hero__information')
    ].filter(Boolean);
    targets.forEach(function (el, i) {
      el.classList.add('cxm-rise');
      if (i === 1) el.classList.add('cxm-rise--2');
      if (i === 2) el.classList.add('cxm-rise--3');
    });
    // double-rAF: class lands after first paint so the transition runs
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        targets.forEach(function (el) { el.classList.add('cxm-in'); });
      });
    });
  }

  /* ── 3 · staggered fade-up on the home grids ── */
  function initStagger() {
    var GROUPS = ['.cx-edit__features', '.cx-stores__grid', '.cx-edit__cats'];
    GROUPS.forEach(function (sel) {
      $all(sel).forEach(function (group) {
        if (!group.children.length) return;
        group.classList.add('cxm-stagger');
        observe(group);
      });
    });
  }

  /* ── 4 · Ken Burns settle on feature imagery ── */
  function initKenBurns() {
    $all('.cx-edit__feature, .cx-edit__float').forEach(function (card) {
      if (!card.querySelector('img')) return;
      card.classList.add('cxm-kenburns');
      observe(card);
    });
  }

  /* ── 1 · spotlight reveal — PULLED (operator, 2026-07-11) ──
     Applied to the editorial it grayscaled the colour campaign image with
     only a cursor-sized colour window ("the now in store is black"). The
     effect needs a deliberately-designed mono surface (e.g. a drop-scene
     page), never an existing colour photograph. Do not re-arm without an
     operator-picked target. */

  /* ── editorial composed entrance (operator pick, 2026-07-11) ──
     Word-mask line-rise on the display title (words grouped into lines by
     measured offsetTop, whole lines staggered .12s), the hand-drawn flourish
     draws itself as the type lands, Ken Burns settle on the photo. The block's
     existing <crux-reveal> whole-fade still runs; this choreographs INSIDE it.
     Touches no Liquid — everything is added and armed here, so it fails open. */
  function initEditorial() {
    $all('.cx-editorial-dazed').forEach(function (block) {
      var title = block.querySelector('.cx-editorial-dazed__title');
      if (title && !title.querySelector('.cxm-w')) {
        // wrap each word of the title's TEXT nodes; the flourish svg stays put
        var nodes = Array.prototype.slice.call(title.childNodes);
        nodes.forEach(function (node) {
          if (node.nodeType !== 3 || !node.textContent.trim()) return;
          var frag = document.createDocumentFragment();
          node.textContent.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (!part.trim()) { frag.appendChild(document.createTextNode(part)); return; }
            var w = document.createElement('span');
            w.className = 'cxm-w';
            var i = document.createElement('span');
            i.className = 'cxm-w__i';
            i.textContent = part;
            w.appendChild(i);
            frag.appendChild(w);
          });
          title.replaceChild(frag, node);
        });
        // group words into lines by measured top → per-LINE stagger
        var words = $all('.cxm-w', title), tops = [];
        words.forEach(function (w) {
          var top = w.offsetTop;
          if (tops.indexOf(top) === -1) tops.push(top);
          w.querySelector('.cxm-w__i').style.setProperty(
            '--cxm-line-delay', (tops.indexOf(top) * 0.12) + 's');
        });
      }
      var flourish = block.querySelector('.cx-editorial-dazed__flourish');
      if (flourish) {
        var path = flourish.querySelector('path');
        if (path) path.setAttribute('pathLength', '1');
        flourish.classList.add('cxm-squiggle');
      }
      var media = block.querySelector('.cx-editorial-dazed__media');
      if (media && media.querySelector('img')) {
        media.classList.add('cxm-kenburns');
        observe(media);
      }
      observe(block);
    });
  }

  /* ── open-now live signal — walk-in attention, not motion ──
     Hours source of truth: cx-org-jsonld (all four stores Mo-Su 10:00-20:00
     Africa/Lagos). Everything JS-built, so no JS = no chip, never a wrong
     chip. If store hours ever diverge (Lagoon Mall / UK) this must switch to
     per-store hours from EON instead of one shared window. */
  function lagosOpen() {
    var hour;
    try {
      hour = parseInt(new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Africa/Lagos', hour: 'numeric', hour12: false
      }).format(new Date()), 10);
    } catch (e) { return null; } // no tz support — no chip beats a wrong chip
    if (isNaN(hour)) return null;
    return { open: hour >= 10 && hour < 20 };
  }

  function buildChip(state) {
    var chip = document.createElement('span');
    chip.className = 'cxm-openchip' + (state.open ? '' : ' cxm-openchip--closed');
    var dot = document.createElement('span');
    dot.className = 'cxm-openchip__dot';
    dot.setAttribute('aria-hidden', 'true');
    chip.appendChild(dot);
    chip.appendChild(document.createTextNode(
      state.open ? 'Stores open now — till 8pm' : 'Stores open 10am–8pm'));
    return chip;
  }

  function initOpenChip() {
    var state = lagosOpen();
    if (!state) return;
    // editorial (NOW IN STORE)
    var block = $all('.cx-editorial-dazed').filter(function (b) {
      return /store/i.test(b.textContent || '');
    })[0];
    if (block && !block.querySelector('.cxm-openchip')) {
      var text = block.querySelector('.cx-editorial-dazed__text');
      if (text) {
        var chip = buildChip(state);
        var readmore = block.querySelector('.c-readmore');
        if (readmore) text.insertBefore(chip, readmore);
        else text.appendChild(chip);
      }
    }
    // COME SEE US store grid (operator, 2026-07-11: "drag attention to this
    // section") — the same chip under the section lede, plus a live status on
    // every store card's hours line.
    $all('.cx-stores').forEach(function (sec) {
      if (sec.querySelector('.cxm-openchip')) return;
      var lede = sec.querySelector('.cx-stores__sub');
      if (lede && lede.parentNode) {
        lede.parentNode.insertBefore(buildChip(state), lede.nextSibling);
      }
      $all('.cx-store__hours', sec).forEach(function (hours) {
        if (hours.querySelector('.cxm-store-live')) return;
        var live = document.createElement('span');
        live.className = 'cxm-store-live' + (state.open ? '' : ' cxm-store-live--closed');
        var d = document.createElement('span');
        d.className = 'cxm-store-live__dot';
        d.setAttribute('aria-hidden', 'true');
        live.appendChild(d);
        live.appendChild(document.createTextNode(state.open ? 'Open now' : 'Opens 10am'));
        hours.appendChild(live);
      });
    });
  }

  /* ── 5 · pressable CTAs ── */
  function initPress() {
    var SEL = '.hero__button, .cx-pcard__viewbtn, ' +
              '.cx-eonrec__add, .cx-edit__cat, .c-stepper__addlook';
    $all(SEL).forEach(function (el) { el.classList.add('cxm-press'); });
  }

  ready(function () {
    try { initLqip(); } catch (e) { /* fail open */ }
    try { initOpenChip(); } catch (e) { /* fail open */ } // signal, not motion
    if (REDUCE) return;
    try { initRise(); } catch (e) { /* fail open */ }
    try { initStagger(); } catch (e) { /* fail open */ }
    try { initKenBurns(); } catch (e) { /* fail open */ }
    try { initEditorial(); } catch (e) { /* fail open */ }
    try { initPress(); } catch (e) { /* fail open */ }
  });
})();
